import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { api } from "@/lib/api";
import { useAuth, formatApiErrorDetail } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { CMS } from "@/constants/testIds/cms";

export function AdminManageModal({ open, onOpenChange }) {
  const { admin } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");

  async function loadAdmins() {
    try {
      const { data } = await api.get("/admins");
      setAdmins(data);
    } catch {
      // ignore
    }
  }

  useEffect(() => {
    if (open) loadAdmins();
  }, [open]);

  async function handleAdd(e) {
    e.preventDefault();
    setError("");
    try {
      await api.post("/auth/register", { email, password, name: name || "Admin" });
      setEmail("");
      setPassword("");
      setName("");
      toast.success("Admin account added");
      loadAdmins();
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  async function handleRemove(id) {
    try {
      await api.delete(`/admins/${id}`);
      toast.success("Admin removed");
      loadAdmins();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="cms-dialog">
        <DialogHeader>
          <DialogTitle>Manage admins</DialogTitle>
          <DialogDescription>Every admin has full, equal access. Add or remove accounts below.</DialogDescription>
        </DialogHeader>
        <ul className="admin-list" data-testid={CMS.adminList}>
          {admins.map((a) => (
            <li key={a.id}>
              <span>
                {a.name} — {a.email}
              </span>
              <button
                type="button"
                onClick={() => handleRemove(a.id)}
                disabled={a.id === admin?.id || admins.length <= 1}
                data-testid={`${CMS.adminRemoveButton}-${a.id}`}
              >
                Remove
              </button>
            </li>
          ))}
        </ul>
        <form onSubmit={handleAdd} className="cms-form">
          <Input placeholder="Name" value={name} onChange={(e) => setName(e.target.value)} data-testid={CMS.adminAddNameInput} />
          <Input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid={CMS.adminAddEmailInput}
          />
          <Input
            type="password"
            placeholder="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid={CMS.adminAddPasswordInput}
          />
          {error && <div className="err">{error}</div>}
          <Button type="submit" data-testid={CMS.adminAddSubmitButton}>
            Add admin
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
